import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

// Mapeo de rutas a módulos de permisos (debe coincidir con el del Sidebar)
const routePermissionMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/pos': 'pos',
  '/cash-register': 'cash_register',
  '/customers': 'customers',
  '/promotions': 'promotions',
  '/products': 'products',
  '/ingredients': 'ingredients',
  '/locations': 'locations',
  '/employees': 'employees',
  '/suppliers': 'suppliers',
  '/stock-config': 'stock_config',
  '/system-parameters': 'system_parameters',
  '/purchase-orders': 'purchase_orders',
  '/distribution': 'distribution',
  '/inventory': 'inventory',
  '/kardex': 'kardex',
  '/transformations': 'transformations',
  '/accounts-payable': 'accounts_payable',
  '/merchandise-movements': 'merchandise_movements',
  '/bread-transfers': 'bread_transfers',
  '/vouchers/manage': 'vouchers',
  '/reports': 'reports'
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePermission?: boolean; // Nuevo prop para habilitar verificación de permisos
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requirePermission = true }) => {
  const { isAuthenticated, selectedLocation, isLoading, user, logout } = useAuth();
  const location = useLocation();

  // Función para verificar si el usuario tiene permisos para la ruta actual
  const hasPermissionForCurrentRoute = (): boolean => {
    if (!requirePermission) return true;

    if (!user?.permisos || !Array.isArray(user.permisos)) {
      return false;
    }

    const requiredModule = routePermissionMap[location.pathname];
    if (!requiredModule) {
      return true; // Si no está mapeado, permitir acceso por defecto
    }

    // Verificar si el usuario tiene permiso para este módulo
    return user.permisos.some((permiso: any) => permiso.module === requiredModule);
  };

  // Debug log para ProtectedRoute
  console.log('🛡️ ProtectedRoute check:', {
    isAuthenticated,
    hasSelectedLocation: !!selectedLocation,
    isLoading,
    currentPath: location.pathname,
    requirePermission,
    hasPermission: hasPermissionForCurrentRoute(),
    userPermissions: user?.permisos?.map((p: any) => p.module)
  });

  if (isLoading) {
    console.log('🛡️ ProtectedRoute: Showing loading...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Cargando...</div>
      </div>
    );
  }

  if (!isAuthenticated || !selectedLocation) {
    console.log('🛡️ ProtectedRoute: Redirecting to login', { isAuthenticated, hasSelectedLocation: !!selectedLocation });
    // Guardar la ruta intentada para redirigir después del login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar permisos del módulo
  if (!hasPermissionForCurrentRoute()) {
    console.log('🛡️ ProtectedRoute: Access denied - insufficient permissions');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Acceso Denegado</h2>
          <p className="text-gray-600 mb-6">No tienes permisos para acceder a este módulo.</p>
          <button
            onClick={async () => {
              await logout();
              window.location.href = '/login';
            }}
            className="px-4 py-2 bg-macartur-primary text-white rounded hover:bg-macartur-accent1 transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  console.log('🛡️ ProtectedRoute: Access granted, rendering children');
  return <>{children}</>;
};